import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Clock, MessageCircle, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate, fmtGNF } from "@/lib/format";
import { ABtn, Panel } from "./AdminLayout";

export default function AdminPaymentReminders() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data: d } = await api.get("/admin/orders/pending-payments");
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remind = async (o) => {
    setBusy(o.id);
    try {
      const { data: r } = await api.post(`/admin/orders/${o.id}/payment-reminder`);
      toast.success(r.email_sent ? `Relance envoyée par e-mail pour ${o.number}` : `Relance enregistrée pour ${o.number}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const whatsapp = (o) =>
    window.open(`https://wa.me/${o.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(o.whatsapp_text)}`, "_blank");

  if (!data) return <p className="text-sm text-slate-400">Chargement…</p>;

  return (
    <div className="space-y-4" data-testid="admin-reminders-page">
      <Panel
        title="Paiements en attente"
        subtitle={`${data.items.length} commande(s) non payée(s) · ${data.overdue} au-delà de 24 h`}
        actions={
          <ABtn variant="ghost" onClick={load} data-testid="reminders-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Actualiser
          </ABtn>
        }
        testid="admin-reminders-panel"
      >
        <p className="mb-3 text-xs text-slate-400" data-testid="reminders-hint">
          Une relance automatique par e-mail et notification est envoyée chaque heure aux commandes non payées
          depuis plus de 24 h (3 relances maximum). Au-delà de <strong>48 h sans paiement, la commande est
          annulée automatiquement</strong> et le stock libéré. Vous pouvez aussi relancer à la main ou par WhatsApp.
        </p>
        <div className="space-y-2">
          {data.items.length === 0 && <p className="text-sm text-slate-500">Aucune commande en attente de paiement.</p>}
          {data.items.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-800 p-3" data-testid={`reminder-row-${o.number}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-num text-sm font-bold text-white">{o.number}</span>
                <span
                  className={`ds-chip ${o.hours_waiting >= 24 ? "border-rose-800 bg-rose-950 text-rose-300" : "border-amber-700 bg-amber-950 text-amber-300"}`}
                  data-testid={`reminder-age-${o.number}`}
                >
                  <Clock className="h-3 w-3" /> {o.hours_waiting} h d'attente
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-300">
                {o.customer_name} · {o.phone} · {o.payment_method_label} · {fmtGNF(o.total)}
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                Commandée le {fmtDate(o.created_at)}
                {o.payment_reference ? ` · réf. annoncée ${o.payment_reference}` : " · aucune référence fournie"}
                {o.reminder_count ? ` · ${o.reminder_count} relance(s)` : ""}
                {o.last_reminder_at ? ` · dernière le ${fmtDate(o.last_reminder_at)}` : ""}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <ABtn onClick={() => remind(o)} disabled={busy === o.id} data-testid={`reminder-send-${o.number}`}>
                  <BellRing className="h-3.5 w-3.5" /> {busy === o.id ? "Envoi…" : "Relancer par e-mail"}
                </ABtn>
                <ABtn variant="gold" onClick={() => whatsapp(o)} data-testid={`reminder-whatsapp-${o.number}`}>
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </ABtn>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
